import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { imagesSummaries } from "../../../data/home/home";

const IMAGE_SIZE = {
  base: "w-[260px] h-[180px] sm:w-[320px] sm:h-[220px] md:w-[400px] md:h-[260px]",
};

const AboutSummarySection = () => {
  const navigate = useNavigate();
  const [topIdx, setTopIdx] = useState(1); // 1: people in front, 0: keyboard in front
  const [isTransitioning, setIsTransitioning] =
    useState(false);
  const [slideDirection, setSlideDirection] =
    useState("down");
  const [isButtonHovered, setIsButtonHovered] =
    useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Swap images with slide effect
  const swapImages = (manual = false) => {
    setIsTransitioning(true);
    setSlideDirection("down");
    setTimeout(() => {
      setTopIdx((prev) => 1 - prev);
      setIsTransitioning(false);
    }, 400); // slide duration
    if (manual) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => swapImages(), 3000);
    }
  };

  // Auto swap every 3 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => swapImages(), 3000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Manual swap on click
  const handleImageSwap = () => {
    swapImages(true);
  };

  return (
    <section className="w-full flex justify-center py-8 md:py-16 bg-transparent dark:bg-black transition-colors duration-300">
      <div className="flex flex-col md:flex-row items-stretch justify-center w-full max-w-7xl px-2 sm:px-4 md:px-8 min-h-[220px]">
        {/* Image Part */}
        <div className="flex-1 flex items-center justify-center min-h-full mb-8 md:mb-0">
          <div
            className="relative min-w-[180px] min-h-[120px] sm:min-w-[260px] sm:min-h-[180px] md:min-w-[400px] md:min-h-[260px] flex-shrink-0"
            style={{ height: "100%" }}
          >
            {/* Keyboard image (behind) */}
            <img
              src={imagesSummaries[0]}
              alt="Keyboard"
              onClick={handleImageSwap}
              className={`absolute top-0 left-0 ${
                IMAGE_SIZE.base
              } object-cover rounded-xl shadow-2xl border-0 cursor-pointer transition-all duration-500
                ${topIdx === 0 ? "z-20" : "z-10"}
                ${
                  isTransitioning && topIdx === 0
                    ? "translate-y-full opacity-0"
                    : ""
                }
                ${
                  !isTransitioning && topIdx === 0
                    ? "translate-y-0 opacity-100"
                    : ""
                }
                ${
                  isTransitioning && topIdx !== 0
                    ? "-translate-y-full opacity-0"
                    : ""
                }
                ${
                  !isTransitioning && topIdx !== 0
                    ? "translate-y-0 opacity-100"
                    : ""
                }
              `}
              style={{
                boxShadow:
                  "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
              }}
            />
            {/* People image (front, overlapping) */}
            <img
              src={imagesSummaries[1]}
              alt="People"
              onClick={handleImageSwap}
              className={`absolute top-10 left-10 sm:top-16 sm:left-16 md:top-20 md:left-20 ${
                IMAGE_SIZE.base
              } object-cover rounded-xl shadow-2xl border-0 cursor-pointer transition-all duration-500
                ${topIdx === 1 ? "z-20" : "z-10"}
                ${
                  isTransitioning && topIdx === 1
                    ? "translate-y-full opacity-0"
                    : ""
                }
                ${
                  !isTransitioning && topIdx === 1
                    ? "translate-y-0 opacity-100"
                    : ""
                }
                ${
                  isTransitioning && topIdx !== 1
                    ? "-translate-y-full opacity-0"
                    : ""
                }
                ${
                  !isTransitioning && topIdx !== 1
                    ? "translate-y-0 opacity-100"
                    : ""
                }
              `}
              style={{
                boxShadow:
                  "0 12px 40px 0 rgba(31, 38, 135, 0.45)",
              }}
            />
          </div>
        </div>
        {/* Text Content Part */}
        <div className="flex-1 flex flex-col justify-center items-start md:pl-10 pl-2 min-h-full">
          <span className="inline-block mb-3 px-4 py-1 rounded-full border border-[#FE6019] text-[#FE6019] bg-[#FE6019]/10 font-semibold text-xs md:text-sm tracking-wide">
            ABOUT US
          </span>
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
            Collaborate to Breakthrough
            <br />
            <span className="font-normal">
              <span className="font-extrabold text-orange-500">
                Join VEA Group to Shape the Future Value
              </span>
            </span>
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4 max-w-2xl text-sm sm:text-base md:text-lg">
            VEA Group - A Multi-Industry Ecosystem Operating
            Locally and Globally, Positioned as a Strategic
            Partner for Businesses on Every Journey of
            Growth and Sustainability.
          </p>
          <p className="text-gray-700 dark:text-gray-300 mb-6 max-w-2xl text-sm sm:text-base md:text-lg">
            With the vision “Optimize Resources - Maximize
            Profits,” VEA Group offers Partners a wide range
            of options to collaborate, grow together, and
            achieve a ‘win-win’ outcome.
          </p>
          <button
            className={`relative flex items-center overflow-hidden font-semibold text-base sm:text-lg shadow-md transition-all duration-300 rounded-lg px-6 py-3 mt-2
              ${
                isButtonHovered
                  ? "bg-black dark:bg-white text-white dark:text-black"
                  : "bg-orange-500 dark:bg-orange-600 text-white"
              }
            `}
            onClick={() => navigate("/about")}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            <span className="z-10 transition-colors duration-300">
              DISCOVER NOW
            </span>
            <span
              className={`flex items-center transition-all duration-300 ml-2 ${
                isButtonHovered
                  ? "translate-x-0 opacity-100"
                  : "translate-x-6 opacity-0"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 12h14M12 5l7 7-7 7"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default AboutSummarySection;
